import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, TrendingUp, TrendingDown, Activity } from "lucide-react";

import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DataTable from "@/components/mui/DataTable";


export default function StockMovements() {
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    product_id: "",
    type: "in",
    quantity: 0,
    reason: "",
  });

  const [pagination, setPagination] = useState({
    page: 1,      // 1-based (Laravel)
    perPage: 15,  // default do backend
    total: 0,
    lastPage: 0,
  });

  useEffect(() => {
    fetchData(1, pagination.perPage);
  }, []);

  const fetchData = async (page = 1, perPage = pagination.perPage) => {
    try {
      setLoading(true);

      const [movementsRes, productsRes] = await Promise.all([
        axios.get(`${API}/stock-movements`, {
          params: { page, per_page: perPage },
        }),
        axios.get(`${API}/products`, {
          params: { per_page: 1000 },
        }),
      ]);

      const movementsData = movementsRes.data;

      if (Array.isArray(movementsData)) {
        // Caso a API não esteja paginada por algum motivo
        setMovements(movementsData);
        setPagination({
          page: 1,
          perPage: movementsData.length,
          total: movementsData.length,
          lastPage: 1,
        });
      } else {
        // Formato padrão do Laravel paginate()
        setMovements(movementsData.data || []);
        setPagination({
          page: movementsData.current_page,
          perPage: movementsData.per_page,
          total: movementsData.total,
          lastPage: movementsData.last_page,
        });
      }

      setProducts(productsRes.data.data || productsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/stock-movements`, formData);
      toast.success("Movimentação registrada!");
      setShowModal(false);
      resetForm();
      // recarrega a página atual com o perPage atual
      fetchData(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Erro ao registrar movimentação"
      );
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: "",
      type: "in",
      quantity: 0,
      reason: "",
    });
  };

  const getProductName = (productId) => {
    const product = products.find((p) => String(p.id) === String(productId));
    return product?.name || "Produto não encontrado";
  };

  const getMovementChipProps = (type) => {
    switch (type) {
      case "in":
        return { label: "Entrada", color: "success", Icon: TrendingUp };
      case "out":
        return { label: "Saída", color: "error", Icon: TrendingDown };
      case "adjust":
        return { label: "Ajuste", color: "warning", Icon: Activity };
      default:
        return { label: type, color: "default", Icon: Activity };
    }
  };

  const handleChangePage = (event, newPageZeroBased) => {
    const newPage = newPageZeroBased + 1; // TablePagination é 0-based
    fetchData(newPage, pagination.perPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      perPage: newPerPage,
      page: 1,
    }));
    fetchData(1, newPerPage);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const selectedProduct =
    products.find(
      (p) => String(p.id) === String(formData.product_id)
    ) || null;

  // Colunas para o DataTable
  const columns = [
    {
      id: "product",
      label: "Produto",
      render: (movement) => (
        <Typography fontWeight={600}>
          {getProductName(movement.product_id)}
        </Typography>
      ),
    },
    {
      id: "type",
      label: "Tipo",
      render: (movement) => {
        const { label, color, Icon } = getMovementChipProps(movement.type);
        return (
          <Chip
            size="small"
            color={color}
            icon={<Icon size={16} />}
            label={label}
          />
        );
      },
    },
    {
      id: "quantity",
      label: "Quantidade",
      align: "center",
    },
    {
      id: "reason",
      label: "Motivo",
      render: (movement) => movement.reason || "-",
    },
    {
      id: "created_at",
      label: "Data",
      render: (movement) =>
        new Date(movement.created_at).toLocaleString("pt-BR"),
    },
  ];

  return (
    <Box data-testid="stock-movements-page">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Movimentações de Estoque
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registre entradas, saídas e ajustes de produtos
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          data-testid="add-movement-btn"
        >
          Nova Movimentação
        </Button>
      </Box>

      <Paper className="data-table-container">
        {/* Tabela / vazio */}
        {pagination.total === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <Activity size={64} />
            <Typography variant="h6">
              Nenhuma movimentação registrada
            </Typography>
            <Typography variant="body2">
              Registre entradas, saídas e ajustes de estoque
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={movements}
            loading={false}
            pagination={{
              page: pagination.page - 1,        // 0-based para o MUI
              rowsPerPage: pagination.perPage,
              total: pagination.total,
              onPageChange: handleChangePage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions: [5, 10, 15, 25, 50],
            }}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `movement-row-${row.id}`}
            emptyMessage="Nenhuma movimentação registrada"
          />
        )}
      </Paper>

      {/* Dialog de nova movimentação */}
      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nova Movimentação</DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {/* Produto - Autocomplete */}
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                options={products}
                value={selectedProduct}
                onChange={(event, value) =>
                  setFormData({
                    ...formData,
                    product_id: value?.id || "",
                  })
                }
                getOptionLabel={(option) =>
                  option?.name
                    ? `${option.name} (Estoque: ${option.stock ?? option.current_stock ?? 0
                    })`
                    : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produto"
                    required
                    size="small"
                    fullWidth
                    data-testid="movement-product-select"
                  />
                )}
                filterOptions={(options, state) =>
                  options.filter((p) => {
                    const term = state.inputValue.toLowerCase();
                    return (
                      p.name.toLowerCase().includes(term) ||
                      (p.barcode &&
                        String(p.barcode).toLowerCase().includes(term))
                    );
                  })
                }
              />
            </Box>

            {/* Tipo */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="movement-type-label">Tipo *</InputLabel>
                <Select
                  labelId="movement-type-label"
                  label="Tipo"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  required
                  data-testid="movement-type-select"
                >
                  <MenuItem value="in">Entrada</MenuItem>
                  <MenuItem value="out">Saída</MenuItem>
                  <MenuItem value="adjust">Ajuste</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Quantidade */}
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Quantidade"
                type="number"
                size="small"
                fullWidth
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    quantity: parseInt(e.target.value, 10) || 0,
                  })
                }
                required
                data-testid="movement-quantity-input"
              />
            </Box>

            {/* Motivo */}
            <Box sx={{ mb: 1 }}>
              <TextField
                label="Motivo"
                size="small"
                fullWidth
                value={formData.reason}
                onChange={(e) =>
                  setFormData({ ...formData, reason: e.target.value })
                }
                placeholder="Ex: Compra, Venda, Correção..."
                data-testid="movement-reason-input"
              />
            </Box>

            <DialogActions sx={{ mt: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                data-testid="cancel-movement-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                data-testid="submit-movement-btn"
              >
                Registrar
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
